/**
 * Cart Drawer Free Gift Modal
 *
 * Bottom-sheet modal for selecting a free gift variant (color + size).
 * Extends HTMLElement following the CartDrawerFreeGift pattern.
 * After add-to-cart, dispatches `cart:update` so the cart drawer re-renders.
 */


import { announceCartLinesUpdate } from '@theme/cart-events';
class CartDrawerFreeGiftModal extends HTMLElement {
  /** @type {HTMLDialogElement|null} */
  #dialog = null;

  /** @type {Object|null} */
  #productData = null;

  /** @type {Object} selected option values keyed by option name */
  #selectedOptions = {};

  /** @type {number|null} */
  #selectedVariantId = null;

  /** @type {boolean} */
  #isAdding = false;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');

    if (!this.#dialog) {
      return;
    }

    this.#dialog.addEventListener('click', this.#handleDialogClick.bind(this));
    this.#dialog.addEventListener('cancel', this.#handleCancel.bind(this));

    const closeBtn = this.querySelector('[ref="closeBtn"]');

    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    const ctaBtn = this.querySelector('[ref="ctaBtn"]');

    if (ctaBtn) {
      ctaBtn.addEventListener('click', () => this.#addToCart());
    }
  }

  /**
   * Open the modal and populate it with product data.
   * @param {Object} productData
   * @param {string} productData.title
   * @param {string} productData.featuredImage
   * @param {Array} productData.options - [{name: 'Color', values: ['Blue','Red']}, ...]
   * @param {Array} productData.variants - [{id, available, options: {Color:'Blue', Size:'M'}, image, swatchColor}]
   */
  open(productData) {
    if (!this.#dialog) {
      return;
    }

    this.#productData = productData;
    this.#selectedOptions = {};
    this.#selectedVariantId = null;

    this.#populateHeader();
    this.#populateOptions();
    this.#selectFirstAvailable();

    this.#dialog.showModal();
  }

  close() {
    if (!this.#dialog || !this.#dialog.open) {
      return;
    }

    this.#dialog.classList.add('dialog-closing');

    const handleAnimationEnd = () => {
      this.#dialog.classList.remove('dialog-closing');
      this.#dialog.close();
      this.#dialog.removeEventListener('animationend', handleAnimationEnd);
    };

    this.#dialog.addEventListener('animationend', handleAnimationEnd);
  }

  #populateHeader() {
    const imgEl = this.querySelector('[ref="productImage"]');
    const titleEl = this.querySelector('[ref="productTitle"]');

    if (imgEl && this.#productData.featuredImage) {
      imgEl.src = this.#productData.featuredImage;
      imgEl.alt = this.#productData.title || '';
    } else if (imgEl) {
      imgEl.src = '';
      imgEl.alt = '';
    }

    if (titleEl) {
      titleEl.textContent = this.#productData.title || '';
    }
  }

  #populateOptions() {
    const colorSection = this.querySelector('[ref="colorSection"]');
    const sizeSection = this.querySelector('[ref="sizeSection"]');
    const swatchesWrap = this.querySelector('[ref="swatchesWrap"]');
    const sizesWrap = this.querySelector('[ref="sizesWrap"]');
    const colorLabel = this.querySelector('[ref="colorLabel"]');
    const sizeLabel = this.querySelector('[ref="sizeLabel"]');

    if (colorSection) {
      colorSection.style.display = 'none';
    }

    if (sizeSection) {
      sizeSection.style.display = 'none';
    }

    if (swatchesWrap) {
      swatchesWrap.innerHTML = '';
    }

    if (sizesWrap) {
      sizesWrap.innerHTML = '';
    }

    if (!this.#productData.options || this.#productData.options.length === 0) {
      return;
    }

    for (const option of this.#productData.options) {
      const isColor = this.#isColorOption(option.name);

      if (isColor && colorSection && swatchesWrap) {
        if (colorLabel) {
          colorLabel.innerHTML = `${option.name}: <strong ref="colorValue"></strong>`;
        }

        for (const value of option.values) {
          const swatchColor = this.#getSwatchColor(value);
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cart-drawer-free-gift-modal__swatch';
          btn.dataset.optionName = option.name;
          btn.dataset.optionValue = value;
          btn.setAttribute('aria-label', value);
          btn.title = value;
          btn.style.setProperty('--swatch-bg', swatchColor);

          btn.addEventListener('click', () => {
            this.#selectOption(option.name, value);
          });

          swatchesWrap.appendChild(btn);
        }

        colorSection.style.display = '';
      } else if (sizeSection && sizesWrap) {
        if (sizeLabel) {
          sizeLabel.innerHTML = `${option.name}: <strong ref="sizeValue"></strong>`;
        }

        for (const value of option.values) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cart-drawer-free-gift-modal__size';
          btn.dataset.optionName = option.name;
          btn.dataset.optionValue = value;
          btn.textContent = value;

          btn.addEventListener('click', () => {
            this.#selectOption(option.name, value);
          });

          sizesWrap.appendChild(btn);
        }

        sizeSection.style.display = '';
      }
    }
  }

  /**
   * Select the first available variant and set its options as selected.
   */
  #selectFirstAvailable() {
    if (!this.#productData || !this.#productData.variants) {
      return;
    }

    const firstAvailable = this.#productData.variants.find((v) => v.available);

    if (!firstAvailable) {
      return;
    }

    for (const [optionName, value] of Object.entries(firstAvailable.options)) {
      this.#selectedOptions[optionName] = value;
    }

    this.#updateUI();
  }

  /**
   * @param {string} optionName
   * @param {string} value
   */
  #selectOption(optionName, value) {
    this.#selectedOptions[optionName] = value;
    this.#updateUI();
  }

  #updateUI() {
    this.#updateVariant();
    this.#updateSwatchSelection();
    this.#updateSizeSelection();
    this.#updateProductImage();
    this.#updateOptionLabels();
    this.#updateCta();
  }

  #updateVariant() {
    if (!this.#productData || !this.#productData.variants) {
      this.#selectedVariantId = null;
      return;
    }

    const match = this.#productData.variants.find((v) => {
      for (const [key, val] of Object.entries(this.#selectedOptions)) {
        if (v.options[key] !== val) {
          return false;
        }
      }

      return true;
    });

    if (match && match.available) {
      this.#selectedVariantId = match.id;
    } else {
      this.#selectedVariantId = null;
    }
  }

  #updateSwatchSelection() {
    const swatches = this.querySelectorAll('.cart-drawer-free-gift-modal__swatch');

    for (const swatch of swatches) {
      const optionName = swatch.dataset.optionName;
      const optionValue = swatch.dataset.optionValue;
      const isSelected = this.#selectedOptions[optionName] === optionValue;

      swatch.classList.toggle('cart-drawer-free-gift-modal__swatch--selected', isSelected);

      const isAvailable = this.#isOptionValueAvailable(optionName, optionValue);
      swatch.classList.toggle('cart-drawer-free-gift-modal__swatch--unavailable', !isAvailable);
    }
  }

  #updateSizeSelection() {
    const sizes = this.querySelectorAll('.cart-drawer-free-gift-modal__size');

    for (const size of sizes) {
      const optionName = size.dataset.optionName;
      const optionValue = size.dataset.optionValue;
      const isSelected = this.#selectedOptions[optionName] === optionValue;

      size.classList.toggle('cart-drawer-free-gift-modal__size--selected', isSelected);

      const isAvailable = this.#isOptionValueAvailable(optionName, optionValue);
      size.classList.toggle('cart-drawer-free-gift-modal__size--unavailable', !isAvailable);
    }
  }

  #updateProductImage() {
    const imgEl = this.querySelector('[ref="productImage"]');

    if (!imgEl || !this.#productData) {
      return;
    }

    const colorOption = this.#productData.options.find((o) => this.#isColorOption(o.name));

    if (!colorOption) {
      return;
    }

    const selectedColor = this.#selectedOptions[colorOption.name];

    if (!selectedColor) {
      return;
    }

    const matchingVariant = this.#productData.variants.find(
      (v) => v.options[colorOption.name] === selectedColor && v.image
    );

    if (matchingVariant && matchingVariant.image) {
      imgEl.src = matchingVariant.image;
    } else if (this.#productData.featuredImage) {
      imgEl.src = this.#productData.featuredImage;
    }
  }

  #updateOptionLabels() {
    for (const option of this.#productData?.options || []) {
      const isColor = this.#isColorOption(option.name);
      const refKey = isColor ? 'colorValue' : 'sizeValue';
      const el = this.querySelector(`[ref="${refKey}"]`);

      if (el) {
        el.textContent = this.#selectedOptions[option.name] || '';
      }
    }
  }

  #updateCta() {
    const ctaBtn = this.querySelector('[ref="ctaBtn"]');

    if (ctaBtn) {
      ctaBtn.disabled = !this.#selectedVariantId || this.#isAdding;
    }
  }

  /**
   * Check if a specific option value is available given the other selected options.
   * @param {string} optionName
   * @param {string} optionValue
   * @returns {boolean}
   */
  #isOptionValueAvailable(optionName, optionValue) {
    if (!this.#productData || !this.#productData.variants) {
      return false;
    }

    return this.#productData.variants.some((v) => {
      if (!v.available) {
        return false;
      }

      if (v.options[optionName] !== optionValue) {
        return false;
      }

      for (const [key, val] of Object.entries(this.#selectedOptions)) {
        if (key === optionName) {
          continue;
        }

        if (v.options[key] !== val) {
          return false;
        }
      }

      return true;
    });
  }

  /**
   * @param {string} name
   * @returns {boolean}
   */
  #isColorOption(name) {
    const lower = name.toLowerCase();
    return lower === 'color' || lower === 'colour' || lower === 'style';
  }

  /**
   * Get the swatch color for a given color option value.
   * @param {string} colorValue
   * @returns {string}
   */
  #getSwatchColor(colorValue) {
    if (!this.#productData || !this.#productData.swatchMap) {
      return '#f3f3f3';
    }

    return this.#productData.swatchMap[colorValue] || '#f3f3f3';
  }

  async #addToCart() {
    if (!this.#selectedVariantId || this.#isAdding) {
      return;
    }

    this.#isAdding = true;
    const ctaBtn = this.querySelector('[ref="ctaBtn"]');
    const originalText = ctaBtn ? ctaBtn.textContent : '';

    if (ctaBtn) {
      ctaBtn.classList.add('is-loading');
      ctaBtn.textContent = '...';
      ctaBtn.disabled = true;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: this.#selectedVariantId, quantity: 1, properties: { '_is_free_gift': 'true' } }],
          sections: this.#getSectionIds(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Failed to add item');
      }

      const data = await response.json();

      this.close();

      await announceCartLinesUpdate({
        action: 'add',
        lines: [{ merchandiseId: String(this.#selectedVariantId), quantity: 1 }],
        source: 'cart-drawer-free-gift-modal',
        sourceId: 'cart-drawer-free-gift-modal',
        sections: data.sections,
        // `/cart/add.js` returns the added line items with their merged quantity — enough for
        // `detail.items`, so the announcement can resolve without a second round trip.
        items: data.items,
        variantId: String(this.#selectedVariantId),
      });
    } catch (error) {
      console.error('[cart-drawer-free-gift-modal]', error.message);

      if (ctaBtn) {
        ctaBtn.textContent = 'Error';
        setTimeout(() => {
          ctaBtn.textContent = originalText;
          ctaBtn.disabled = false;
        }, 1500);
      }
    } finally {
      this.#isAdding = false;

      if (ctaBtn) {
        ctaBtn.classList.remove('is-loading');
      }
    }
  }

  /**
   * @returns {string}
   */
  #getSectionIds() {
    const cartItemsComponent = document.querySelector('cart-items-component[data-section-id]');

    if (cartItemsComponent) {
      return cartItemsComponent.dataset.sectionId || '';
    }

    return '';
  }

  /**
   * Close on backdrop click (click on the dialog element itself, not its children).
   * @param {MouseEvent} event
   */
  #handleDialogClick(event) {
    if (event.target === this.#dialog) {
      const rect = this.#dialog.getBoundingClientRect();
      const clickedInsideDialog =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;

      if (!clickedInsideDialog) {
        this.close();
      }
    }
  }

  /**
   * Handle ESC key — browser fires cancel event before closing dialog.
   * @param {Event} event
   */
  #handleCancel(event) {
    event.preventDefault();
    this.close();
  }
}

if (!customElements.get('cart-drawer-free-gift-modal')) {
  customElements.define('cart-drawer-free-gift-modal', CartDrawerFreeGiftModal);
}
